import { CITIES } from '@/lib/site'
import { matchAreaToFilterOption } from '@/lib/propertyFilterUtils'
import { buildPropertyHref } from '@/lib/propertyNavLinks'
import type { PropertyFilters, PropertyIntent } from '@/types/property'

const STOP_WORDS = new Set([
  'in', 'at', 'for', 'the', 'a', 'an', 'near', 'and', 'or', 'to', 'of', 'on',
  'property', 'properties', 'listing', 'listings', 'real', 'estate', 'home', 'homes',
])

/** Words that must never become an area/locality filter. */
const GENERIC_AREA_WORDS = new Set([
  'property', 'properties', 'listing', 'listings', 'real', 'estate', 'home', 'homes',
  'residential', 'commercial', 'industrial', 'agricultural', 'farm',
  'plot', 'plots', 'land', 'project', 'projects', 'booking', 'bookings',
  'sale', 'sell', 'buy', 'rent', 'rental', 'lease', 'purchase',
  'new', 'type', 'bhk', 'verified', 'qbc', 'weekend', 'house', 'houses',
  'flat', 'flats', 'shop', 'shops', 'office', 'offices',
  'fully', 'semi', 'furnished', 'unfurnished', 'furnish',
])

/** Listing amenity phrases — never locality / never free-text area URL. */
const AMENITY_PHRASE_RE =
  /\b(?:fully|semi(?:[-\s]*)?|un[-\s]*)?furnished\b|\bunfurnished\b/gi

/** Prefer exact UI category labels when possible. */
const CATEGORY_PHRASES: Array<{ pattern: RegExp; category: string }> = [
  { pattern: /\bresidential\s+(?:land\s+or\s+)?plots?\b|\bresidential\s+land\b/i, category: 'Residential Land or Plot' },
  { pattern: /\bcommercial\s+(?:land\s+or\s+)?plots?\b|\bcommercial\s+land\b/i, category: 'Commercial Land or Plot' },
  { pattern: /\bindustrial\s+(?:land\s+or\s+)?plots?\b|\bindustrial\s+land\b/i, category: 'Industrial Land or Plot' },
  { pattern: /\bagricultural\s+land\b/i, category: 'Agricultural Land' },
  { pattern: /\bcommercial\s+projects?\b/i, category: 'Commercial Projects' },
  { pattern: /\bnew\s+projects?\b/i, category: 'New Project' },
  { pattern: /\bfarm\s+houses?\b/i, category: 'Farm House' },
  { pattern: /\bweekend\s+houses?\b/i, category: 'Weekend House' },
  { pattern: /\bhotel(?:s)?\s*(?:and|&)?\s*restaurants?\b|\bhotels?\b|\brestaurants?\b/i, category: 'Hotel and Restaurant' },
  { pattern: /\bcold\s+storages?\b/i, category: 'Cold Storage' },
  { pattern: /\bshed(?:s)?\s*(?:or|&)?\s*warehouses?\b|\bwarehouses?\b|\bsheds?\b/i, category: 'Shed or Warehouse' },
]

const CATEGORY_HINTS: Record<string, string> = {
  house: 'Houses',
  houses: 'Houses',
  home: 'Houses',
  flat: 'Flats',
  flats: 'Flats',
  apartment: 'Flats',
  apartments: 'Flats',
  villa: 'Houses',
  villas: 'Houses',
  plot: 'Residential Land or Plot',
  plots: 'Residential Land or Plot',
  land: 'Residential Land or Plot',
  shop: 'Shops',
  shops: 'Shops',
  office: 'Offices',
  offices: 'Offices',
  showroom: 'Shops',
  showrooms: 'Shops',
  warehouse: 'Shed or Warehouse',
  warehouses: 'Shed or Warehouse',
  bungalow: 'Houses',
  bunglow: 'Houses',
  tenement: 'Houses',
  factory: 'Factory',
  commercial: 'Commercial Projects',
}

const INTENT_RENT_RE = /\b(for\s+)?(rent|rental|lease)\b/i
const INTENT_BUY_RE = /\b(for\s+)?(buy|sale|sell|purchase)\b/i
const COMMERCIAL_INTENT_RE = /\b(commercial|industrial|factory)\b/i
const COMMERCIAL_TYPE_RE = /\b(shops?|offices?|showrooms?|warehouses?)\b/i

const RENT_ONLY = new Set(['rent', 'rental', 'lease', 'for rent'])
const BUY_ONLY = new Set(['buy', 'sale', 'sell', 'purchase', 'for sale', 'for sell'])

function stripIntentWords(text: string, pattern: RegExp) {
  return text.replace(pattern, ' ').replace(/\s+/g, ' ').trim()
}

/** Pull buy/rent intent out of free-text search (e.g. "2bhk rent rajkot"). */
export function extractIntentFromText(
  text: string,
  fallbackIntent: string,
): { intent: PropertyIntent; remainder: string; explicit: boolean } {
  const trimmed = text.trim()
  const lower = trimmed.toLowerCase()

  if (RENT_ONLY.has(lower)) {
    return { intent: 'rent', remainder: '', explicit: true }
  }
  if (BUY_ONLY.has(lower)) {
    return { intent: 'buy', remainder: '', explicit: true }
  }

  if (INTENT_RENT_RE.test(trimmed)) {
    const remainder = stripIntentWords(trimmed, INTENT_RENT_RE)
    return { intent: 'rent', remainder, explicit: true }
  }
  if (INTENT_BUY_RE.test(trimmed)) {
    const remainder = stripIntentWords(trimmed, INTENT_BUY_RE)
    return { intent: 'buy', remainder, explicit: true }
  }
  if (COMMERCIAL_INTENT_RE.test(trimmed)) {
    const remainder = stripIntentWords(trimmed, COMMERCIAL_INTENT_RE)
    return { intent: 'commercial', remainder, explicit: true }
  }
  // Keep shop/office/etc. in remainder so extractCategory can set the type filter.
  if (COMMERCIAL_TYPE_RE.test(trimmed)) {
    return { intent: 'commercial', remainder: trimmed, explicit: true }
  }

  return {
    intent: (fallbackIntent as PropertyIntent) || 'buy',
    remainder: trimmed,
    explicit: false,
  }
}

export interface ParsedSearchQuery {
  city: string
  search?: string
  bhk?: string
  category?: string
  area?: string
  saleType?: string
  verified?: boolean
  intent?: PropertyIntent
  /** True when the user typed rent/buy/commercial in the query (not from the tab). */
  intentExplicit?: boolean
}

/** Area name only — show every property type in that locality, no auto category/intent. */
export function isAreaOnlySearch(parsed: ParsedSearchQuery): boolean {
  return Boolean(parsed.area?.trim())
    && !parsed.category
    && !parsed.bhk
    && !parsed.saleType
    && !parsed.search?.trim()
    && !parsed.intentExplicit
}

/**
 * Only put intent in the URL when the user typed buy/rent/sale/commercial words.
 * "2bhk properties in rajkot" must not force Buy — show all listing types for that BHK.
 */
function shouldOmitIntentInUrl(parsed: ParsedSearchQuery): boolean {
  return !parsed.intentExplicit
}

export interface PropertiesSearchUrlOptions {
  intent: string
  defaultCity: string
  premium?: boolean
  tabCategory?: string
  saleType?: string
  /**
   * Suggestion chips (e.g. “for Rent”) may force intent into the URL even when
   * the typed query has no buy/rent words. Main search submit leaves this false.
   */
  forceIntent?: boolean
  /**
   * Master area names for the selected city. Area is only written to the URL
   * when the candidate matches one of these — never invent area= from adjectives.
   */
  knownAreas?: string[]
}

function normalize(value: string) {
  return value.trim().toLowerCase()
}

/** Match a free-text place name to a configured Gujarat city. */
export function matchKnownCity(name: string) {
  const needle = normalize(name)
  return CITIES.find(
    (city) => normalize(city.name) === needle || city.slug === needle.replace(/\s+/g, '-'),
  )
}

export function cityLabelFromSlug(slug: string) {
  return matchKnownCity(slug)?.name ?? slug.replace(/\b\w/g, (c) => c.toUpperCase())
}

function matchCity(name: string) {
  return matchKnownCity(name)
}

/** Pull a known Gujarat city out of free-text (e.g. "... in Rajkot"). */
function extractCity(text: string, defaultCity: string) {
  let remainder = text.trim()
  let city = defaultCity

  const inMatch = remainder.match(/\bin\s+([a-z][a-z\s-]{1,40})$/i)
  if (inMatch?.[1]) {
    const hit = matchCity(inMatch[1].trim())
    if (hit) {
      city = hit.slug
      remainder = remainder.slice(0, inMatch.index).trim()
      return { city, remainder }
    }
  }

  // Whole-query city when user types only "rajkot" or "ahmedabad".
  const whole = matchCity(remainder)
  if (whole) {
    return { city: whole.slug, remainder: '' }
  }

  // City as a token anywhere: "properties rajkot", "2bhk surat"
  for (const known of CITIES) {
    const re = new RegExp(`\\b${known.name.replace(/\s+/g, '\\s+')}\\b|\\b${known.slug}\\b`, 'i')
    if (re.test(remainder)) {
      city = known.slug
      remainder = remainder.replace(re, ' ').replace(/\s+/g, ' ').trim()
      return { city, remainder }
    }
  }

  return { city, remainder }
}

function extractBhk(text: string) {
  if (/\bstudio\b/i.test(text)) {
    return {
      bhk: 'Studio' as string | undefined,
      remainder: text.replace(/\bstudio\b/gi, ' ').replace(/\s+/g, ' ').trim(),
    }
  }

  const plusMatch = text.match(/(\d+)\s*\+\s*bh?k?/i)
  if (plusMatch) {
    const n = Number(plusMatch[1])
    return {
      bhk: Number.isFinite(n) ? (n >= 5 ? '5+ BHK' : `${n}+ BHK`) : '5+ BHK',
      remainder: text.replace(plusMatch[0], ' ').replace(/\s+/g, ' ').trim(),
    }
  }

  const match = text.match(/(\d+)\s*bh?k?/i)
  if (!match) return { bhk: undefined as string | undefined, remainder: text }
  const n = Number(match[1])
  const label = Number.isFinite(n) ? (n >= 6 ? '5+ BHK' : `${n} BHK`) : `${match[1]} BHK`
  const allowed = new Set(['1 BHK', '2 BHK', '3 BHK', '4 BHK', '5 BHK', '5+ BHK', 'Studio'])
  if (!allowed.has(label)) return { bhk: undefined as string | undefined, remainder: text }
  return {
    bhk: label,
    remainder: text.replace(match[0], ' ').replace(/\s+/g, ' ').trim(),
  }
}

function extractSaleType(text: string) {
  const resaleRe = /\b(re-?sale|resale)\b/i
  const newRe = /\b(new\s+bookings?|new\s+booking|new\s+property)\b/i
  const isNewProjectPhrase = /\bnew\s+projects?\b/i.test(text)
  if (resaleRe.test(text)) {
    return {
      saleType: 'Old Property' as string | undefined,
      remainder: text.replace(resaleRe, ' ').replace(/\s+/g, ' ').trim(),
      isNewProjectPhrase: false,
    }
  }
  if (newRe.test(text)) {
    return {
      saleType: 'New Property' as string | undefined,
      remainder: text.replace(newRe, ' ').replace(/\s+/g, ' ').trim(),
      isNewProjectPhrase,
    }
  }
  return { saleType: undefined as string | undefined, remainder: text, isNewProjectPhrase: false }
}

function extractVerified(text: string) {
  const verifiedRe = /\b(qbc\s+verified|verified)\b/i
  if (!verifiedRe.test(text)) {
    return { verified: false, remainder: text }
  }
  return {
    verified: true,
    remainder: text.replace(verifiedRe, ' ').replace(/\s+/g, ' ').trim(),
  }
}

function extractSort(text: string) {
  const relevanceRe = /\b(relevance|relevant)\b/i
  if (!relevanceRe.test(text)) return text
  return text.replace(relevanceRe, ' ').replace(/\s+/g, ' ').trim()
}

function extractCategory(text: string, tabCategory?: string) {
  let category = tabCategory
  let remainder = text

  // Multi-word property-type phrases first (e.g. "residential plot").
  for (const phrase of CATEGORY_PHRASES) {
    if (phrase.pattern.test(remainder)) {
      category = category ?? phrase.category
      remainder = remainder.replace(phrase.pattern, ' ').replace(/\s+/g, ' ').trim()
    }
  }

  for (const [hint, slug] of Object.entries(CATEGORY_HINTS)) {
    const re = new RegExp(`\\b${hint}\\b`, 'i')
    if (re.test(remainder)) {
      category = category ?? slug
      remainder = remainder.replace(re, ' ').replace(/\s+/g, ' ').trim()
    }
  }

  // Drop leftover type-qualifier words so they never become an area.
  remainder = remainder
    .replace(/\b(residential|commercial|industrial|agricultural)\b/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim()

  return { category, remainder }
}

function normalizeAreaSegment(value: string) {
  return value
    .replace(/^(?:by|the)\s+/i, '')
    .replace(/\s+/g, ' ')
    .trim()
}

/** True when the text is only generic words like "property" / "properties". */
export function isGenericAreaName(value?: string): boolean {
  if (!value?.trim()) return true
  const tokens = normalizeAreaSegment(value)
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
  if (tokens.length === 0) return true
  return tokens.every((t) => STOP_WORDS.has(t) || GENERIC_AREA_WORDS.has(t))
}

/** Strip noisy prefixes/punctuation from area/locality names. */
export function normalizeAreaName(area?: string): string | undefined {
  if (!area?.trim()) return undefined
  const cleaned = normalizeAreaSegment(area.trim())
    .replace(/[,.;:]+$/g, '')
    .replace(/\s+/g, ' ')
    .trim()
  if (!cleaned || isGenericAreaName(cleaned)) return undefined
  return cleaned
}

/**
 * True when the query looks like a pasted listing title
 * (e.g. "Showroom for Rent on Nehru Nagar Main Road, Rajkot").
 * Those must stay free-text search — not forced into wrong area/category filters.
 */
export function isListingTitleQuery(raw: string): boolean {
  const t = raw.trim().replace(/\s+/g, ' ')
  if (!t) return false
  const words = t.split(' ').filter(Boolean)

  // "Showroom for Rent on …" / "Flat for Sale in Ambika Park…"
  // Exclude short filter queries like "flat for sale in rajkot".
  const titleShape =
    /\b(?:showroom|shops?|offices?|flats?|apartments?|houses?|villas?|tenements?|bungalows?|plots?|land|warehouses?|factory)\b.{0,48}\bfor\s+(?:rent|sale|sell)\s+(?:on|in|at|near)\b/i
  if (titleShape.test(t) && words.length >= 6) {
    const shortCityOnly =
      words.length <= 6 &&
      /\bin\s+[a-z][a-z\s-]{1,40}$/i.test(t) &&
      !/,/.test(t) &&
      !/\b(road|marg|street|chowk|society|nagar|park|hub|dam)\b/i.test(t)
    if (!shortCityOnly) return true
  }

  // Long comma-separated listing titles.
  if (
    words.length >= 8 &&
    /,/.test(t) &&
    /\b(?:for\s+)?(?:rent|sale|sell)\b/i.test(t) &&
    /\b(?:road|marg|street|chowk|society|nagar)\b/i.test(t)
  ) {
    return true
  }

  return false
}

function extractArea(text: string) {
  const t = text.trim()
  if (!t || isGenericAreaName(t)) {
    return { area: undefined as string | undefined, remainder: isGenericAreaName(t) ? '' : t }
  }

  // Avoid treating a recognized city as an area.
  if (matchCity(t)) {
    return { area: undefined as string | undefined, remainder: t }
  }

  const nearMatch = t.match(/\b(?:near|at)\s+([a-z][a-z0-9\s-]{2,60})/i)
  if (nearMatch?.[1]) {
    const area = normalizeAreaName(nearMatch[1])
    const remainder = t.replace(nearMatch[0], ' ').replace(/\s+/g, ' ').trim()
    return { area, remainder: isGenericAreaName(remainder) ? '' : remainder }
  }

  // After city extraction, leftover "in <locality>" should become area.
  const inMatch = t.match(/\bin\s+(.+)$/i)
  if (inMatch?.[1]) {
    const place = inMatch[1].trim()
    if (!matchCity(place)) {
      const area = normalizeAreaName(place)
      const remainder = t.slice(0, inMatch.index).trim()
      return { area, remainder: isGenericAreaName(remainder) ? '' : remainder }
    }
  }

  // Bare leftover after type/intent (e.g. "shop mavdi", "aaji dam").
  // May become an area *candidate* for matching — URL only emits area= after
  // knownAreas validation (see buildPropertiesSearchUrl).
  const bare = cleanupSearch(t)
  if (bare && bare.split(/\s+/).length <= 4) {
    const area = normalizeAreaName(bare)
    if (area) return { area, remainder: '' }
  }

  return { area: undefined as string | undefined, remainder: isGenericAreaName(t) ? '' : t }
}

function cleanupSearch(text: string) {
  const tokens = text
    .split(/\s+/)
    .map((t) => t.trim())
    .filter((t) => t.length > 1 && !STOP_WORDS.has(t.toLowerCase()))

  return tokens.length > 0 ? tokens.join(' ') : undefined
}

/**
 * Turn a natural-language hero query into structured listing filters.
 * Example: "3bhk house in rajkot" → city=rajkot, bhk=3 BHK, category=house.
 */
export function parseHeroSearchQuery(
  raw: string,
  defaultCity: string,
  options?: { tabCategory?: string; tabIntent?: string },
): ParsedSearchQuery {
  const trimmed = raw.trim()
  const hasNewProjectPhrase = /\bnew\s+projects?\b/i.test(trimmed)
  if (!trimmed) {
    return { city: normalize(defaultCity), intent: (options?.tabIntent as PropertyIntent) ?? 'buy' }
  }

  // Pasted listing titles: keep free-text search + intent/city/BHK only.
  // Forcing category/area from title words zeros results (wrong type or road≠locality).
  if (isListingTitleQuery(trimmed)) {
    const { intent, remainder: afterIntent, explicit: intentExplicit } = extractIntentFromText(
      trimmed,
      options?.tabIntent ?? 'buy',
    )
    const afterAmenities = afterIntent.replace(AMENITY_PHRASE_RE, ' ').replace(/\s+/g, ' ').trim()
    const { city, remainder: afterCity } = extractCity(afterAmenities, normalize(defaultCity))
    const { bhk, remainder: afterBhk } = extractBhk(afterCity)
    const search = cleanupSearch(afterBhk.replace(/[,.;:]/g, ' '))
    return {
      city,
      search,
      bhk,
      category: undefined,
      area: undefined,
      intent,
      intentExplicit,
    }
  }

  const { intent, remainder: afterIntent, explicit: intentExplicit } = extractIntentFromText(
    trimmed,
    options?.tabIntent ?? 'buy',
  )
  const afterAmenities = afterIntent.replace(AMENITY_PHRASE_RE, ' ').replace(/\s+/g, ' ').trim()
  const { city, remainder: afterCity } = extractCity(afterAmenities, normalize(defaultCity))
  const { bhk, remainder: afterBhk } = extractBhk(afterCity)
  const { saleType, remainder: afterSaleType } = extractSaleType(afterBhk)
  const { verified, remainder: afterVerified } = extractVerified(afterSaleType)
  const { category, remainder: afterCategory } = extractCategory(afterVerified, options?.tabCategory)
  const normalizedAfterCategory = hasNewProjectPhrase
    ? afterCategory.replace(/\bnew\s+projects?\b/gi, ' ').replace(/\s+/g, ' ').trim()
    : afterCategory
  const { area, remainder: afterArea } = extractArea(normalizedAfterCategory)
  const remainder = extractSort(afterArea)
  const search = cleanupSearch(remainder)

  const finalCategory =
    hasNewProjectPhrase ? 'New Project' : category

  return {
    city,
    search,
    bhk,
    category: finalCategory,
    area,
    saleType,
    verified: verified || undefined,
    intent,
    intentExplicit,
  }
}

/** Build a property listing URL from a hero/mobile search box query. */
export function buildPropertiesSearchUrl(
  rawQuery: string,
  opts: PropertiesSearchUrlOptions,
): string {
  const parsed = parseHeroSearchQuery(rawQuery, opts.defaultCity, {
    tabCategory: opts.tabCategory,
    tabIntent: opts.intent,
  })

  const omitIntent = shouldOmitIntentInUrl(parsed) && !opts.forceIntent
  const intent = omitIntent
    ? ''
    : parsed.intentExplicit
      ? (parsed.intent ?? opts.intent)
      : opts.intent

  // Area URL only when the candidate matches a real city locality.
  // Otherwise keep as free-text search — listing page may promote if it matches later.
  const areaCandidate = normalizeAreaName(parsed.area)
  let search = parsed.search
  let area: string | undefined
  if (areaCandidate) {
    const known = opts.knownAreas?.filter(Boolean) ?? []
    const matched = known.length > 0 ? matchAreaToFilterOption(areaCandidate, known) : null
    if (matched) {
      area = matched
    } else {
      // No master list, or no match — never invent area= from free text.
      search = cleanupSearch([areaCandidate, search].filter(Boolean).join(' '))
    }
  }

  return buildPropertyHref(intent === 'commercial' ? 'commercial' : intent === 'rent' ? 'rent' : intent === 'buy' ? 'buy' : '', {
    city: parsed.city,
    bhk: parsed.bhk,
    category: parsed.category ?? opts.tabCategory,
    area,
    search: search || undefined,
    saleType: parsed.saleType || opts.saleType,
    verified: parsed.verified ? 'true' : undefined,
    premium: opts.premium ? 'true' : undefined,
  })
}

/** When area is set, drop BHK/search tokens wrongly copied from a listing suggestion title. */
export function reconcileAreaSearchFilters(filters: PropertyFilters): Partial<PropertyFilters> | null {
  const rawAreas = filters.area ?? []
  const normalizedAreas = rawAreas
    .map((a) => {
      const trimmed = a.trim()
      if (/^\d+$/.test(trimmed)) return trimmed
      return normalizeAreaName(a)
    })
    .filter((a): a is string => Boolean(a))

  const areaChanged = normalizedAreas.length !== rawAreas.length
    || normalizedAreas.some((a, i) => a !== rawAreas[i])

  const hasArea = normalizedAreas.length > 0
  const searchLooksLikeListingTitle = Boolean(
    filters.search && /\b\d+\s*bhk\b/i.test(filters.search),
  )
  const shouldDropBhk = hasArea && (filters.bhk?.length ?? 0) > 0 && searchLooksLikeListingTitle
  const shouldDropSearch = hasArea && searchLooksLikeListingTitle

  if (!areaChanged && !shouldDropBhk && !shouldDropSearch) return null

  return {
    area: normalizedAreas,
    ...(shouldDropBhk ? { bhk: [] } : {}),
    ...(shouldDropSearch ? { search: undefined } : {}),
  }
}

/** Fix URLs like `intent=buy&search=rent` so the listing page shows the right inventory. */
export function reconcileIntentFromSearch(filters: PropertyFilters): Partial<PropertyFilters> | null {
  const raw = filters.search?.trim()
  if (!raw) return null

  const lower = raw.toLowerCase()
  const hasIntentHint =
    RENT_ONLY.has(lower) ||
    BUY_ONLY.has(lower) ||
    INTENT_RENT_RE.test(raw) ||
    INTENT_BUY_RE.test(raw)
  if (!hasIntentHint) return null

  const { intent, remainder } = extractIntentFromText(raw, filters.intent ?? 'buy')
  const nextSearch = cleanupSearch(remainder)
  const intentChanged = intent !== filters.intent
  const searchChanged = (nextSearch ?? undefined) !== (filters.search ?? undefined)

  if (!intentChanged && !searchChanged) return null

  return {
    intent,
    search: nextSearch,
  }
}
